<template>
  <div class="row g-2">
    <div class="col-12 col-lg-7">
      <div v-bind="getRootProps()" class="hs-drop" :class="{ 'hs-drop-active': isDragActive, 'hs-drop-busy': isReading }">
        <input v-bind="getInputProps()">
        <div v-if="isReading" class="hs-drop-inner">
          <div class="spinner-border text-primary mb-1" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <h4 class="hs-drop-title">Checking your file</h4>
          <p class="hs-muted mb-1">{{ fileName }}</p>
          <div class="hs-progress"><span class="hs-progress-bar"></span></div>
        </div>
        <div v-if="! isReading" class="hs-drop-inner">
          <span class="hs-drop-icon"><i class="fas fa-file-excel"></i></span>
          <h4 class="hs-drop-title">Drop your filled Excel file here</h4>
          <p class="hs-muted mb-75">or</p>
          <span class="btn btn-outline-primary hs-browse"><i class="fas fa-folder-open me-50"></i>Browse file</span>
          <div class="hs-chips">
            <span class="hs-chip"><i class="fas fa-check-circle"></i>.xlsx only</span>
            <span class="hs-chip"><i class="fas fa-check-circle"></i>Exported from this budget</span>
          </div>
        </div>
      </div>
    </div>
    <div class="col-12 col-lg-5">
      <div class="hs-guide">
        <p class="hs-guide-title">3 simple steps</p>
        <ol class="hs-timeline">
          <li class="hs-timeline-item">
            <span class="hs-timeline-dot">1</span>
            <div class="hs-timeline-body">
              <p class="hs-item-title">Download the Excel</p>
              <p class="hs-item-text">Every head and period of this tab is already in it.</p>
              <button type="button" class="btn btn-primary btn-sm" :disabled="isExporting" @click="emit('onExport')">
                <span v-if="isExporting" class="spinner-border spinner-border-sm me-50" role="status"></span>
                <span v-if="! isExporting"><i class="fas fa-download me-50"></i></span>Export this budget
              </button>
            </div>
          </li>
          <li class="hs-timeline-item">
            <span class="hs-timeline-dot">2</span>
            <div class="hs-timeline-body">
              <p class="hs-item-title">{{ typeStep.title }}</p>
              <p class="hs-item-text">{{ typeStep.text }}</p>
              <div class="hs-legend">
                <span v-for="item in legend" :key="item.name" class="hs-legend-item">
                  <span class="hs-swatch" :class="item.swatch"></span>{{ item.name }}
                </span>
              </div>
            </div>
          </li>
          <li class="hs-timeline-item">
            <span class="hs-timeline-dot">3</span>
            <div class="hs-timeline-body">
              <p class="hs-item-title">Upload and check</p>
              <p class="hs-item-text mb-0">See every change before anything is saved.</p>
            </div>
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue'
import { useDropzone } from 'vue3-dropzone'

const props = defineProps({
  tab: { type: String, default: 'item' },
  isReading: { type: Boolean, default: false },
  isExporting: { type: Boolean, default: false },
  fileName: { type: String, default: '' }
})

const emit = defineEmits(['onFileSelected', 'onExport'])

const showError = inject('showError')

const amountStep = { title: 'Type Qty and Rate, or only the Amount', text: 'Amount = Qty x Rate. The period split fills in by itself.' }

const typeSteps = {
  item: { title: 'Type Qty and Rate', text: 'Amount and the period split fill in by themselves.' },
  account: amountStep,
  activity_budget: amountStep,
  activity_target: { title: 'Type Qty', text: 'The period and team member split fill in by themselves.' }
}

const typeStep = computed(() => typeSteps[props.tab] || typeSteps.item)

const legend = [
  { name: 'You type', swatch: 'hs-swatch-input' },
  { name: 'Fills itself', swatch: 'hs-swatch-auto' },
  { name: 'Do not change', swatch: 'hs-swatch-locked' }
]

const onDrop = (acceptedFiles) => {
  if (acceptedFiles.length < 1) {
    showError('Please choose one Excel file (.xlsx).')
    return
  }

  emit('onFileSelected', acceptedFiles[0])
}

const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: '.xlsx', multiple: false })
</script>

<style scoped>
.hs-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 340px;
  padding: 2rem 1.5rem;
  border: 2px dashed #cdd3e0;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  transition: border-color .15s ease, background-color .15s ease, box-shadow .15s ease, transform .15s ease;
}

.hs-drop:hover,
.hs-drop-active {
  border-color: #7367f0;
  background: #f6f5ff;
  box-shadow: 0 8px 24px rgba(115, 103, 240, .12);
}

.hs-drop-active {
  transform: translateY(-2px);
}

.hs-drop-busy {
  cursor: progress;
}

.hs-drop-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.hs-drop-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 84px;
  margin-bottom: 1.1rem;
  border-radius: 50%;
  background: #e3f4ea;
  color: #1d6f42;
  font-size: 2.4rem;
  box-shadow: 0 0 0 10px rgba(29, 111, 66, .06);
}

.hs-drop-title {
  margin-bottom: .35rem;
  font-weight: 600;
  color: #212529;
}

.hs-muted {
  color: #4b5563;
}

.hs-browse {
  border-radius: 8px;
}

.hs-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: .5rem;
  margin-top: 1.25rem;
}

.hs-chip {
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  padding: .25rem .7rem;
  border-radius: 999px;
  background: #f3f4f8;
  color: #4b5563;
  font-size: .86rem;
  font-weight: 500;
}

.hs-chip i {
  color: #28c76f;
}

.hs-progress {
  width: 220px;
  height: 4px;
  overflow: hidden;
  border-radius: 4px;
  background: #ebe9f1;
}

.hs-progress-bar {
  display: block;
  width: 40%;
  height: 100%;
  border-radius: 4px;
  background: #7367f0;
  animation: hs-slide 1.2s ease-in-out infinite;
}

@keyframes hs-slide {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(250%);
  }
}

.hs-guide {
  height: 100%;
  padding: 1.4rem 1.5rem;
  border: 1px solid #eef1f6;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 24px rgba(34, 41, 47, .06);
}

.hs-guide-title {
  margin-bottom: 1.1rem;
  padding-bottom: .6rem;
  border-bottom: 1px dashed #e6ebf5;
  font-size: .86rem;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: .3px;
}

.hs-timeline {
  margin: 0;
  padding: 0;
  list-style: none;
}

.hs-timeline-item {
  position: relative;
  display: flex;
  gap: .9rem;
  padding-bottom: 1.4rem;
}

.hs-timeline-item:last-child {
  padding-bottom: 0;
}

.hs-timeline-item:not(:last-child)::before {
  content: '';
  position: absolute;
  top: 30px;
  bottom: 2px;
  left: 13px;
  width: 2px;
  background: #e6e4fb;
}

.hs-timeline-dot {
  display: inline-flex;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #eeedfd;
  color: #7367f0;
  font-size: .9rem;
  font-weight: 700;
}

.hs-timeline-body {
  min-width: 0;
}

.hs-item-title {
  margin-bottom: .15rem;
  font-weight: 600;
  color: #212529;
}

.hs-item-text {
  margin-bottom: .6rem;
  font-size: .92rem;
  color: #4b5563;
}

.hs-legend {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem .9rem;
}

.hs-legend-item {
  display: inline-flex;
  align-items: center;
  gap: .4rem;
  font-size: .88rem;
  color: #4b5563;
}

.hs-swatch {
  width: 16px;
  height: 16px;
  border: 1px solid #cdd3e0;
  border-radius: 4px;
}

.hs-swatch-input {
  background: #fff9db;
}

.hs-swatch-auto {
  background: #ddebf7;
}

.hs-swatch-locked {
  background: #f2f2f2;
}

@media (max-width: 991.98px) {
  .hs-drop {
    min-height: 260px;
  }
}
</style>
